import { RefObject, useCallback, useLayoutEffect, useRef } from "react";

// Horizontal scrolling of the grid around its frozen columns. The frozen
// (sticky) columns cover the left part of the scroll container: the scrollable
// columns are only visible between their right edge and the right edge of the
// container. The grid declares the width of the frozen columns as the
// scroll-padding-left of its scroll container, so that the browser also leaves
// them out when it scrolls an element into view (focus, autofocus...).

// Width (px) of the bands, right of the frozen columns and at the right edge of
// the grid, where dragging a column header scrolls the grid
export const DRAG_SCROLL_ZONE = 60;
// Scroll speed (px per frame) when the dragged header is at the outer edge of a band
export const DRAG_SCROLL_MAX_SPEED = 20;

// Below this offset (px) a cell is considered visible (sub-pixel layout)
const REVEAL_TOLERANCE = 1;

// revealScrollDelta returns the scrollLeft change making a cell fully visible
// between visibleLeft (the right edge of the frozen columns) and visibleRight,
// with the smallest move. A cell wider than the room left is aligned on the
// frozen columns.
export function revealScrollDelta(cellLeft: number, cellRight: number, visibleLeft: number, visibleRight: number): number {
    if (cellLeft < visibleLeft - REVEAL_TOLERANCE || cellRight - cellLeft > visibleRight - visibleLeft) {
        return cellLeft - visibleLeft;
    }
    if (cellRight > visibleRight + REVEAL_TOLERANCE) {
        return cellRight - visibleRight;
    }
    return 0;
}

// dragScrollSpeed returns the horizontal scroll speed (px per frame, negative
// to the left) for a column header dragged at pointerX: the grid scrolls left
// when the pointer is in the band right of the frozen columns (visibleLeft),
// right in the band at the right edge of the grid (visibleRight), faster as the
// pointer gets closer to the frozen columns or to the edge. Nothing scrolls
// over the frozen columns, where the column may be dropped.
export function dragScrollSpeed(pointerX: number, visibleLeft: number, visibleRight: number): number {
    if (pointerX < visibleLeft || pointerX > visibleRight) {
        return 0;
    }
    // The bands never take more than a third of the scrollable part each
    const zone = Math.min(DRAG_SCROLL_ZONE, (visibleRight - visibleLeft) / 3);
    if (zone <= 0) {
        return 0;
    }
    if (pointerX < visibleLeft + zone) {
        return -Math.ceil(DRAG_SCROLL_MAX_SPEED * (visibleLeft + zone - pointerX) / zone);
    }
    if (pointerX > visibleRight - zone) {
        return Math.ceil(DRAG_SCROLL_MAX_SPEED * (pointerX - (visibleRight - zone)) / zone);
    }
    return 0;
}

// scrollableBounds returns the horizontal viewport coordinates between which
// the scrollable columns of the grid are visible: right of the frozen columns
// (the scroll-padding-left of the container) and left of its vertical scrollbar.
export function scrollableBounds(scroller: HTMLElement): { left: number, right: number } {
    const left = scroller.getBoundingClientRect().left + scroller.clientLeft;
    const frozenWidth = parseFloat(getComputedStyle(scroller).scrollPaddingLeft) || 0;
    return { left: left + frozenWidth, right: left + scroller.clientWidth };
}

// revealGridCell scrolls the grid horizontally so that the cell holding element
// is fully visible right of the frozen columns. A frozen cell is always visible.
export function revealGridCell(scroller: HTMLElement, element: Element | null) {
    const cell = element?.closest("td");
    if (!cell || !scroller.contains(cell) || getComputedStyle(cell).position === "sticky") {
        return;
    }
    const bounds = scrollableBounds(scroller);
    const rect = cell.getBoundingClientRect();
    const delta = revealScrollDelta(rect.left, rect.right, bounds.left, bounds.right);
    if (delta !== 0) {
        scroller.scrollLeft += delta;
    }
}

// useScrollLeftKeptAcrossClear brings the grid back to its columns when its lines
// are dropped before being fetched again (clearData): left without columns, it
// has nothing to scroll and the browser moves it back to the start. The returned
// function follows the position of the grid, from its scroll event.
export function useScrollLeftKeptAcrossClear(scrollerRef: RefObject<HTMLElement>, hasColumns: boolean): (scroller: HTMLElement) => void {
    const scrollLeftRef = useRef(0);
    const hadColumnsRef = useRef(hasColumns);

    useLayoutEffect(() => {
        const columnsAreBack = hasColumns && !hadColumnsRef.current;
        hadColumnsRef.current = hasColumns;
        if (columnsAreBack && scrollerRef.current) {
            scrollerRef.current.scrollLeft = scrollLeftRef.current;
        }
    }, [hasColumns]);

    return useCallback((scroller: HTMLElement) => {
        // The moves of a grid without columns are not the ones of the user
        if (hadColumnsRef.current) {
            scrollLeftRef.current = scroller.scrollLeft;
        }
    }, []);
}
